import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  modalTimeoutConfig,
  SessionTimeoutButtons,
} from '../models/modal-timeout.config';
import { TimeoutModalComponent } from '../timeout-modal/timeout-modal.component';

// TODO explicit to timeoutModule
@Injectable()
export class SessionTimeoutService {
  showDialog = false;
  isUserIdleRunning = false;
  lastPing?: Date = null;
  userIdleSettings = {
    idleinSec: environment.userIdleSettings.idle,
    timeoutInSec: environment.userIdleSettings.timeout,
    warningDialogInSec: environment.userIdleSettings.warningDialog,
    tokenRefreshInSec: environment.userIdleSettings.tokenRefreshInterval,
  };
  private lastButtonClicked = new BehaviorSubject<SessionTimeoutButtons>(null);
  lastButtonClickedObs = this.lastButtonClicked.asObservable();
  timeRemaining =
    this.userIdleSettings.timeoutInSec -
    this.userIdleSettings.warningDialogInSec;
  dialogRef: MatDialogRef<TimeoutModalComponent>;
  IDLE_NAME = 'user-session';
  IDLE_LOCAL_STORAGE_KEY = `ng2Idle.${this.IDLE_NAME}.expiry`;

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private dialog: MatDialog
  ) {}

  isSessionIdleExpired() {
    let isExpired = true;
    if (window.localStorage) {
      const expiryValue = localStorage.getItem(this.IDLE_LOCAL_STORAGE_KEY);
      if (expiryValue) {
        const expiryDate = new Date(+expiryValue);
        // if expiry date is greater than the current date
        if (expiryDate > new Date()) {
          isExpired = false;
        }
      }
    }
    return isExpired;
  }

  configIdle() {
    this.idle.setIdleName(this.IDLE_NAME);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // sets an idle timeout in seconds
    this.idle.setIdle(this.userIdleSettings.idleinSec);
    // sets a timeout period in seconds
    this.idle.setTimeout(this.userIdleSettings.timeoutInSec);
    // sets refresh token interval
    this.keepalive.interval(this.userIdleSettings.tokenRefreshInSec);
  }

  userTimeout() {
    this.isUserIdleRunning = false;
    this.idle.stop();
    if (this.showDialog && this.dialogRef && this.dialogRef.componentInstance) {
      this.dialogRef.componentInstance.data.isSessionExpired = true;
    }
  }

  openDialog() {
    this.showDialog = true;
    this.idle.clearInterrupts();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = modalTimeoutConfig.panelClass;
    dialogConfig.backdropClass = modalTimeoutConfig.backdropClass;
    dialogConfig.data = {
      isSessionExpired: false,
      minutesRemaining: Math.floor((this.timeRemaining % 3600) / 60),
      secondsRemaining: Math.floor((this.timeRemaining % 3600) % 60),
      exitButton: SessionTimeoutButtons.exitSession,
      extendSessionButton: SessionTimeoutButtons.extendSession,
      pastTimeoutButton: SessionTimeoutButtons.pastTimeout,
    };
    this.dialogRef = this.dialog.open(TimeoutModalComponent, dialogConfig);
    this.dialogRef
      .afterClosed()
      .subscribe((buttonInfo: SessionTimeoutButtons) => {
        this.lastButtonClicked.next(buttonInfo);
      });
  }

  closeDialog() {
    this.showDialog = false;
    this.dialogRef = null;
    this.timeRemaining =
      this.userIdleSettings.timeoutInSec -
      this.userIdleSettings.warningDialogInSec;
    // sets the interrupts like Keydown, scroll, mouse wheel, mouse down, and etc
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  }

  checkTimeoutWarning(countdown: number) {
    //If dialog is visible, update the countdown
    if (this.showDialog && this.dialogRef && this.dialogRef.componentInstance) {
      this.timeRemaining = countdown;
      this.dialogRef.componentInstance.data.minutesRemaining = Math.floor(
        (this.timeRemaining % 3600) / 60
      );
      this.dialogRef.componentInstance.data.secondsRemaining = Math.floor(
        (this.timeRemaining % 3600) % 60
      );
    }

    if (
      !this.showDialog &&
      this.userIdleSettings.timeoutInSec - countdown >=
        this.userIdleSettings.warningDialogInSec
    ) {
     this.openDialog();
    }

  }

  extendSession() {
    this.configIdle();
    this.idle.watch();
  }

  startWatch() {
    this.isUserIdleRunning = true;
    this.idle.watch();
  }

  stopWatch() {
    this.isUserIdleRunning = false;
    this.idle.stop();
  }
}
